import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Header from "../../components/Header";
import { getMyProfile, updateMyProfile } from "../../services/userService";

const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};
const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

const UserProfile = () => {
  const theme = useTheme();
  const palette = useMemo(
    () => (theme.palette.mode === "dark" ? PALETTE_DARK : PALETTE_LIGHT),
    [theme.palette.mode]
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    name: "",
    lastName: "",
    hireDate: "",
    revenuePercentage: "",
    licenseNumber: "",
    password: "",
  });

  const formatDateOnly = (value) => {
    if (!value) return "";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
  };

  const inputSx = {
    "& .MuiInputLabel-root": { color: palette.label },
    "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
    "& .MuiInputBase-input": { color: palette.text },
    "& .MuiInputBase-input.Mui-focused": { color: palette.text },
    "& .MuiOutlinedInput-root": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: palette.cardBorder,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: palette.label,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: palette.accent,
        borderWidth: 2,
      },
    },
  };

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getMyProfile();
        setProfile(data);
        setFormData({
          username: data.username || "",
          name: data.name || "",
          lastName: data.lastName || "",
          hireDate: formatDateOnly(data.hireDate),
          revenuePercentage:
            data.revenuePercentage !== null && data.revenuePercentage !== undefined
              ? data.revenuePercentage
              : "",
          licenseNumber: data.licenseNumber || "",
          password: "",
        });
      } catch (err) {
        setError(err?.message || "No se pudo cargar el perfil");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const normalizedUsername = formData.username?.trim();
    if (!normalizedUsername) {
      setError("El usuario es obligatorio.");
      return;
    }

    const payload = { username: normalizedUsername };
    if (formData.password) payload.password = formData.password;

    if (profile?.type === "Boss" || profile?.type === "Driver") {
      payload.name = formData.name?.trim();
      payload.lastName = formData.lastName?.trim();
    }
    if (profile?.type === "Driver") {
      payload.hireDate = formData.hireDate ? formData.hireDate : null;
      payload.revenuePercentage =
        formData.revenuePercentage === "" ? undefined : Number(formData.revenuePercentage);
      if (
        payload.revenuePercentage !== undefined &&
        (Number.isNaN(payload.revenuePercentage) ||
          payload.revenuePercentage < 0 ||
          payload.revenuePercentage > 100)
      ) {
        setError("El porcentaje de recaudación debe estar entre 0 y 100.");
        return;
      }
    }
    if (profile?.type === "LicenseUser") {
      payload.licenseNumber = formData.licenseNumber?.trim();
    }

    try {
      setSaving(true);
      const updated = await updateMyProfile(payload);
      setProfile(updated);
      setFormData({
        username: updated.username || "",
        name: updated.name || "",
        lastName: updated.lastName || "",
        hireDate: formatDateOnly(updated.hireDate),
        revenuePercentage:
          updated.revenuePercentage !== null && updated.revenuePercentage !== undefined
            ? updated.revenuePercentage
            : "",
        licenseNumber: updated.licenseNumber || "",
        password: "",
      });
      setSuccess("Perfil actualizado correctamente.");
    } catch (err) {
      setError(err?.message || "No se pudo actualizar el perfil");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ m: "20px", display: "flex", flexDirection: "column", gap: 2 }}>
      <Header title="Perfil de Usuario" subtitle="Consulta o edita tu información personal" />

      <Box
        sx={{
          backgroundColor: palette.cardBg,
          border: `1px solid ${palette.cardBorder}`,
          borderRadius: "14px",
          p: { xs: 2, md: 3 },
          boxShadow:
            palette === PALETTE_LIGHT
              ? "0 8px 22px rgba(2,6,23,0.08)"
              : "0 3px 10px rgba(0,0,0,0.24)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          gap={1}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: palette.title, fontWeight: 800 }}>
              Datos de cuenta
            </Typography>
            <Typography variant="body2" sx={{ color: palette.label }}>
              Los cambios se guardarán para tu usuario actual.
            </Typography>
          </Box>
          {profile?.type && (
            <Chip
              label={profile.type}
              sx={{
                bgcolor: "transparent",
                border: `1px solid ${palette.accent}`,
                color: palette.text,
                fontWeight: 700,
              }}
            />
          )}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Usuario"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  sx={inputSx}
                />
              </Grid>

              {(profile?.type === "Boss" || profile?.type === "Driver") && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nombre"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Apellidos"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      sx={inputSx}
                    />
                  </Grid>
                </>
              )}

              {profile?.type === "Driver" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Fecha de alta"
                      name="hireDate"
                      type="date"
                      value={formData.hireDate}
                      onChange={handleChange}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="% de recaudación"
                      name="revenuePercentage"
                      type="number"
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      value={formData.revenuePercentage}
                      onChange={handleChange}
                      sx={inputSx}
                    />
                  </Grid>
                </>
              )}

              {profile?.type === "LicenseUser" && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Número de licencia"
                    name="licenseNumber"
                    value={formData.licenseNumber}
                    onChange={handleChange}
                    sx={inputSx}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nueva contraseña (opcional)"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  sx={inputSx}
                />
              </Grid>
            </Grid>

            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end" gap={1.5} sx={{ mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                sx={{
                  backgroundColor: palette.btn,
                  color: "white",
                  fontWeight: 700,
                  textTransform: "none",
                  minWidth: 160,
                  "&:hover": { backgroundColor: palette.btnHover },
                }}
              >
                {saving ? "Guardando..." : "Guardar cambios"}
              </Button>
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UserProfile;
